import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FaTiktok, FaFacebookF, FaLinkedinIn, FaGoogle, FaStar, FaSnapchatGhost } from 'react-icons/fa';
import logo from 'assets/images/logo.png';
import { postRequest } from '../../services/api/apiService';
import { isValidEmail } from '../../utils/validators';
import { setAuthToken } from 'utils/auth';
import EditorialTheme from '../Home/EditorialTheme';

const ACCENT = '#818CF8';

// ── Compact product preview (monochrome navy) ──────────────────────
function ProductPreview() {
  return (
    <div style={{ background: '#16122E', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
      {/* Title bar */}
      <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 6 }}>
        {[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(255,255,255,0.15)' }} />)}
        <span className="mono-label" style={{ marginLeft: 8, fontSize: 9, color: 'rgba(255,255,255,0.35)' }}>Campaign Manager — OneBotAds</span>
        <div style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: ACCENT, boxShadow: `0 0 6px ${ACCENT}` }} />
      </div>
      {/* Stats row */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        {[{ l: 'Total Spend', v: '$1,204' }, { l: 'Impressions', v: '781K' }, { l: 'Avg CTR', v: '3.5%' }].map((s, i) => (
          <div key={i} style={{ flex: 1, padding: '10px 12px', borderRight: i < 2 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>{s.l}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', fontFamily: "'Space Grotesk',sans-serif" }}>{s.v}</div>
          </div>
        ))}
      </div>
      {/* Campaign rows */}
      {[
        { name: 'Summer Sale — App Installs', plat: 'TikTok', status: 'ACTIVE', spend: '$892', trend: '+12%', active: true },
        { name: 'Lead Gen — Morocco Q2', plat: 'Meta', status: 'ACTIVE', spend: '$312', trend: '+7%', active: false },
        { name: 'B2B Decision Makers', plat: 'LinkedIn', status: 'PAUSED', spend: '$0', trend: '—', active: false },
      ].map((c, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: c.active ? 'rgba(255,255,255,0.04)' : 'transparent', borderLeft: c.active ? `2px solid ${ACCENT}` : '2px solid transparent' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: c.active ? '#fff' : 'rgba(255,255,255,0.55)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', marginTop: 1 }}>{c.plat}</div>
          </div>
          <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, fontWeight: 700, background: c.status === 'ACTIVE' ? 'rgba(129,140,248,0.15)' : 'rgba(255,255,255,0.06)', color: c.status === 'ACTIVE' ? ACCENT : 'rgba(255,255,255,0.4)', flexShrink: 0 }}>{c.status}</span>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>{c.spend}</div>
            <div style={{ fontSize: 9, color: c.trend !== '—' ? ACCENT : 'rgba(255,255,255,0.25)' }}>{c.trend}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

const PLATFORM_ICONS = [
  { icon: <FaTiktok />, color: '#69C9D0' },
  { icon: <FaFacebookF />, color: '#1877f2' },
  { icon: <FaGoogle />, color: '#4285f4' },
  { icon: <FaLinkedinIn />, color: '#0a66c2' },
  { icon: <FaSnapchatGhost />, color: '#FFFC00' },
  { icon: <span style={{ display: 'inline-flex', width: 13, height: 13, borderRadius: 3, background: '#1862D9', color: '#fff', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 900, fontFamily: 'Georgia,serif' }}>t</span>, color: '#1862D9' },
];

const FEATURES = [
  'Launch on 6 platforms at once',
  'AI writes your ad copy instantly',
  'Live analytics across all accounts',
];

const Login = () => {
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [errors, setErrors]         = useState({ email: false, password: false });
  const [loginError, setLoginError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const emailInvalid = !trimmedEmail || !isValidEmail(trimmedEmail);
    const newErrors = { email: emailInvalid, password: !password };
    setErrors(newErrors);
    if (newErrors.email || newErrors.password) {
      if (emailInvalid && trimmedEmail) setLoginError('Please enter a valid email address.');
      return;
    }
    try {
      const response = await postRequest('/api/auth/login', { email: trimmedEmail, password });
      if (response?.token) { setAuthToken(response.token); navigate('/dashboard'); }
    } catch (error) {
      setLoginError(error.response?.data?.message || 'Invalid email or password.');
    }
  };

  const handleGoogleLoginSuccess = async (response) => {
    try {
      const authResponse = await postRequest('/api/auth/google-login', { token: response.credential });
      if (authResponse?.authToken) { setAuthToken(authResponse.authToken); navigate('/dashboard'); }
    } catch (error) {
      setLoginError(error.response?.data?.message || 'Google login failed.');
    }
  };

  return (
    <div className="obhome" style={{ minHeight: '100vh', display: 'flex' }}>
      <EditorialTheme />
      <style>{`
        @keyframes authFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        .auth-input {
          width: 100%; padding: 12px 14px 12px 42px;
          border: 1.5px solid #E5E6EF; border-radius: 10px;
          font-size: 14px; color: #111; background: #F6F7FB;
          outline: none; box-sizing: border-box;
          transition: border 0.2s, box-shadow 0.2s, background 0.2s;
        }
        .auth-input::placeholder { color: #9CA3AF; }
        .auth-input:focus { border-color: #221C51; background: #fff; box-shadow: 0 0 0 3px rgba(34,28,81,0.09); }
        .auth-input.error { border-color: #EF4444; background: #fff; }
        .auth-submit-btn {
          width: 100%; padding: 14px; border-radius: 999px;
          background: #221C51; color: #fff; font-size: 15px; font-weight: 700;
          border: none; cursor: pointer; transition: all 0.2s ease;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          font-family: 'Space Grotesk', sans-serif;
        }
        .auth-submit-btn:hover { background: #2D2570; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(34,28,81,0.25); }
        @media (max-width: 900px) { .auth-left-panel { display: none !important; } }
      `}</style>

      {/* ── LEFT BRAND PANEL (editorial navy) ── */}
      <div className="auth-left-panel" style={{
        width: '52%',
        background: '#0F0B2D',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '40px 52px 36px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Faint light */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'radial-gradient(ellipse 80% 50% at 30% 0%, rgba(255,255,255,0.05) 0%, transparent 60%)', pointerEvents: 'none' }} />

        {/* Top: Logo + headline */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Link to="/" style={{ display: 'inline-flex', background: '#fff', borderRadius: 8, padding: '6px 10px', marginBottom: 36 }}>
            <img src={logo} alt="OneBotAds" style={{ height: 26, display: 'block' }} />
          </Link>
          <div className="mono-label" style={{ color: 'rgba(255,255,255,0.4)', marginBottom: 18 }}>
            <span style={{ color: ACCENT }}>/</span> Welcome back
          </div>
          <h2 style={{ fontSize: 'clamp(22px,2.4vw,32px)', fontWeight: 700, color: '#fff', lineHeight: 1.15, marginBottom: 12, letterSpacing: '-0.03em' }}>
            Your entire ad stack,{' '}
            <em className="serif-accent" style={{ color: '#A5B4FC' }}>unified.</em>
          </h2>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.65, marginBottom: 26, maxWidth: 360 }}>
            TikTok, Meta, Google, LinkedIn, Snapchat &amp; Taboola — launch cross-platform campaigns in minutes with AI.
          </p>

          {/* Feature list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
            {FEATURES.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 18, height: 18, borderRadius: 5, background: 'rgba(129,140,248,0.15)', border: '1px solid rgba(129,140,248,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: ACCENT, flexShrink: 0 }}>✓</span>
                <span style={{ fontSize: 13.5, fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Middle: Product mockup */}
        <div style={{ position: 'relative', zIndex: 1, animation: 'authFloat 6s ease-in-out infinite', marginBottom: 28 }}>
          <ProductPreview />
        </div>

        {/* Bottom: social proof */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Platform icons */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 18, alignItems: 'center' }}>
            <span className="mono-label" style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginRight: 4 }}>Works with</span>
            {PLATFORM_ICONS.map((p, i) => (
              <div key={i} style={{ width: 30, height: 30, borderRadius: 9, background: '#16122E', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: p.color }}>
                {p.icon}
              </div>
            ))}
          </div>

          {/* Testimonial */}
          <div style={{ padding: '14px 16px', background: 'rgba(255,255,255,0.04)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.09)' }}>
            <div style={{ display: 'flex', gap: 2, marginBottom: 8 }}>
              {[1,2,3,4,5].map(i => <FaStar key={i} style={{ fontSize: 10, color: ACCENT }} />)}
            </div>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, marginBottom: 10 }}>
              "ONEBOTADS took us from 4 hours per campaign down to under 30 minutes. Incredible."
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', fontFamily: "'Space Grotesk',sans-serif" }}>MR</div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>Marcus Rivera</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Head of Digital · Nexus eCommerce</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ── */}
      <div style={{
        flex: 1,
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 32px',
      }}>
        {/* Mobile logo */}
        <Link to="/" style={{ display: 'none', marginBottom: 28 }} className="mobile-logo-auth">
          <img src={logo} alt="OneBotAds" style={{ height: 32 }} />
        </Link>
        <style>{`@media(max-width:900px){.mobile-logo-auth{display:flex!important;}}`}</style>

        <div style={{ width: '100%', maxWidth: 380 }}>
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: '#111', marginBottom: 6, letterSpacing: '-0.03em' }}>Welcome back</h1>
            <p style={{ fontSize: 14, color: '#737373' }}>
              Don't have an account?{' '}
              <Link to="/signup" style={{ color: '#4F46E5', fontWeight: 600, textDecoration: 'none' }}>Sign up free</Link>
            </p>
          </div>

          {loginError && (
            <div style={{ padding: '11px 14px', borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: 13, marginBottom: 20 }}>
              {loginError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#404040', marginBottom: 7 }}>Email address</label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13, pointerEvents: 'none' }} />
                <input type="email" value={email} onChange={e => { setEmail(e.target.value); setErrors(v => ({ ...v, email: false })); setLoginError(''); }} placeholder="you@example.com" className={`auth-input${errors.email ? ' error' : ''}`} />
              </div>
              {errors.email && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 5 }}>Please enter a valid email address</p>}
            </div>

            <div style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#404040' }}>Password</label>
                <Link to="/forgot-password" style={{ fontSize: 12, color: '#4F46E5', fontWeight: 500, textDecoration: 'none' }}>Forgot password?</Link>
              </div>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faLock} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13, pointerEvents: 'none' }} />
                <input type="password" value={password} onChange={e => { setPassword(e.target.value); setErrors(v => ({ ...v, password: false })); setLoginError(''); }} placeholder="••••••••" className={`auth-input${errors.password ? ' error' : ''}`} autoComplete="current-password" />
              </div>
              {errors.password && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 5 }}>Password is required</p>}
            </div>

            <div style={{ marginBottom: 22 }} />

            <button type="submit" className="auth-submit-btn">
              Log in <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12 }} />
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0' }}>
            <div style={{ flex: 1, height: 1, background: '#E5E6EF' }} />
            <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>or</span>
            <div style={{ flex: 1, height: 1, background: '#E5E6EF' }} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <GoogleLogin onSuccess={handleGoogleLoginSuccess} onError={() => setLoginError('Google login failed. Please try again.')} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
